import { OfferTermsBlockField, TermsConditionType } from "../constants";
import { getMappingIds, isApplyAll } from "./offerMapping";

/** True for null, undefined, or empty string. */
export const isBlank = (value) => value === null || value === undefined || value === "";

/** Formats a date for offer UI lists, or "-" when missing. */
export const formatDate = (date) => {
	if (!date) return "-";
	return new Date(date).toLocaleDateString();
};

/** Formats a scalar for the details page, using "-" for empty values. */
export const displayValue = (value) => {
	if (value === 0) return "0";
	if (isBlank(value)) return "-";
	return String(value);
};

/** Trims paragraph terms content to a display string. */
export const getParagraphText = (content) => String(content || "").trim();

/** Returns non-empty trimmed items from an ordered-list terms block. */
export const getListItems = (content) =>
	(Array.isArray(content) ? content : [])
		.map((item) => String(item || "").trim())
		.filter(Boolean);

/** True when a terms block has a type and non-empty content. */
export const isTermsBlockComplete = (block) => {
	if (!block?.[OfferTermsBlockField.TYPE]) return false;
	if (block[OfferTermsBlockField.TYPE] === TermsConditionType.ORDERED_LIST) {
		return getListItems(block[OfferTermsBlockField.CONTENT]).length > 0;
	}
	if (block[OfferTermsBlockField.TYPE] === TermsConditionType.PARAGRAPH) {
		return Boolean(getParagraphText(block[OfferTermsBlockField.CONTENT]));
	}
	return false;
};

/** True when a terms block has content worth showing on the details page. */
export const isRenderableTermsBlock = (block) => {
	const type = block?.[OfferTermsBlockField.TYPE];
	const content = block?.[OfferTermsBlockField.CONTENT];
	if (type === TermsConditionType.PARAGRAPH) {
		return Boolean(getParagraphText(content));
	}
	if (type === TermsConditionType.ORDERED_LIST) {
		return getListItems(content).length > 0;
	}
	return false;
};

/** Formats one mapping entry as a label, preferring name unless preferId is set. */
export const formatMappingEntryLabel = (entry, { preferId = false } = {}) => {
	if (entry == null || entry === "") return "";
	if (typeof entry !== "object") return String(entry);
	const id = entry.id != null ? String(entry.id).trim() : "";
	if (preferId) return id;
	const name = entry.name != null ? String(entry.name).trim() : "";
	return name || id;
};

/**
 * Formats a mapping field as "All", "None", a joined label list, or "N selected".
 * Pass labels for id→name lookup; maxShown truncates long lists for overview.
 */
export const formatMappingDisplay = (
	value,
	{ labels = {}, maxShown, preferId = false } = {}
) => {
	if (isApplyAll(value)) return "All";
	const ids = getMappingIds(value);
	if (!ids.length) return "None";
	const named = ids
		.map((entry) => {
			if (typeof entry !== "object" && labels[entry]) return labels[entry];
			return formatMappingEntryLabel(entry, { preferId });
		})
		.filter(Boolean);
	if (!named.length) return "None";
	if (maxShown != null && named.length > maxShown) {
		return `${named.length} selected`;
	}
	return named.join(", ");
};
